/**
 * Request animation frames on demand. Works similarly to `Debounce` class, but it asks for
 * animation frames.
 *
 * If there is a `frameId` already set to the instance, we cancel it.
 *
 * After the `fn` (the function that is given during the construction of this class) is called,
 * the `frameId` is set to `null` again so another animation frame can be called.
 *
 * The class simply manages the frames using `requestAnimationFrame` and `cancelAnimationFrame`
 * functions. Providing a more declarative way of dealing with animation frames without using
 * these functions directly inside components.
 */
export default class AnimationFrame {
  private frameId: number | null;
  public constructor(private readonly fn: () => void) {}
  public cancel() {
    if (this.frameId === null) {
      return;
    }
    cancelAnimationFrame(this.frameId);
    this.frameId = null;
  }
  public run() {
    this.cancel();
    this.frameId = requestAnimationFrame(() => {
      try {
        this.fn();
      } catch (reason) {
        console.error(
          'Function passed to AnimationFrame instance failed when called with the ' +
            'following error: %o',
          reason
        );
      }
      this.frameId = null;
    });
  }
}
